/**
 * Feature flag configuration for page visibility
 *
 * Uses VITE_ENABLED_PAGES environment variable to control which pages are visible.
 * This is a whitelist approach - only pages listed in the env var will be accessible.
 *
 * @example
 * // .env.development
 * VITE_ENABLED_PAGES=dashboard,prospectDesk,orderHub,vehicleHub
 *
 * // .env.production
 * VITE_ENABLED_PAGES=dashboard,prospectDesk,orderHub
 */

/**
 * Available page identifiers for feature flagging
 */
export type PageId =
  | 'dashboard'
  | 'inbox'
  | 'notifications'
  | 'trackOrders'
  | 'admin'
  | 'prospectDesk'
  | 'orderHub'
  | 'partsCenter'
  | 'serviceCenter'
  | 'claimsCenter'
  | 'vehicleHub'
  | 'customerCare'
  | 'registrationDesk'
  | 'accountsDesk';

/**
 * Parse enabled pages from environment variable
 */
const getEnabledPages = (): Set<string> => {
  const enabledPagesEnv = import.meta.env.VITE_ENABLED_PAGES || '';
  const pages = enabledPagesEnv
    .split(',')
    .map((page: string) => page.trim().toLowerCase())
    .filter(Boolean);
  return new Set(pages);
};

const enabledPages = getEnabledPages();

/**
 * Check if a page is enabled based on VITE_ENABLED_PAGES environment variable
 * @param pageId - The page identifier to check
 * @returns true if the page is enabled, false otherwise
 * @example
 * ```ts
 * if (isPageEnabled('claimsCenter')) {
 *   // Render claims center routes
 * }
 * ```
 */
export const isPageEnabled = (pageId: PageId): boolean => {
  // If no pages specified, enable all (for backwards compatibility)
  if (enabledPages.size === 0) {
    return true;
  }
  return enabledPages.has(pageId.toLowerCase());
};

/**
 * Filter an array of routes based on enabled pages
 * @param routes - Array of route objects with pageId property
 * @returns Filtered array of routes that are enabled
 */
export const filterEnabledRoutes = <T extends { pageId?: PageId }>(routes: T[]): T[] => {
  return routes.filter((route) => !route.pageId || isPageEnabled(route.pageId));
};

/**
 * Maps sidebar navigation IDs (kebab-case) to PageId (camelCase)
 */
const sidebarIdToPageId: Record<string, PageId> = {
  'admin-panel': 'admin',
  'prospect-desk': 'prospectDesk',
  'order-hub': 'orderHub',
  'parts-center': 'partsCenter',
  'service-center': 'serviceCenter',
  'claims-center': 'claimsCenter',
  'vehicle-hub': 'vehicleHub',
  'customer-care': 'customerCare',
  'registration-desk': 'registrationDesk',
  'accounts-desk': 'accountsDesk',
};

/**
 * Check if a navigation item is enabled based on its ID (feature flag + entitlements).
 *
 * @param navigationId - The sidebar navigation ID (kebab-case)
 * @param entitlements - Entitlements array from useEntitlements()
 * @returns true if the navigation item is enabled for the current user
 */
export const isNavigationEnabled = (navigationId: string, entitlements?: string[]): boolean => {
  const pageId = sidebarIdToPageId[navigationId];
  if (pageId && !isPageEnabled(pageId)) return false;

  if (entitlements === undefined) return true;

  return entitlements.includes(navigationId);
};

/**
 * Controls whether the Engagement Hub tab is shown in the Prospect Details view.
 * Defaults to false — must be explicitly enabled via VITE_IS_ENGAGEMENT_HUB_ENABLED=true.
 * @returns true if the Engagement Hub feature is enabled
 */
export const isEngagementHubEnabled = (): boolean => {
  return import.meta.env.VITE_IS_ENGAGEMENT_HUB_ENABLED === 'true';
};

/**
 * Controls whether the MY27 (2027 model year) Charging Credit Offer is available at checkout.
 * Defaults to false — must be explicitly enabled via VITE_IS_MY27_ENABLED=true.
 * When disabled, the MY26 (2026) charging voucher remains unaffected.
 * @returns true if the MY27 charging voucher feature is enabled
 */
export const isMy27Enabled = (): boolean => {
  return import.meta.env.VITE_IS_MY27_ENABLED === 'true';
};

/**
 * Controls whether the Profile Settings page is accessible from the avatar in the
 * Settings drawer. Defaults to false — must be explicitly enabled via
 * VITE_IS_PROFILE_SETTINGS_ENABLED=true. When disabled, the avatar is not clickable.
 * @returns true if the Profile Settings page is enabled
 */
export const isProfileSettingsEnabled = (): boolean => {
  return import.meta.env.VITE_IS_PROFILE_SETTINGS_ENABLED === 'true';
};

/**
 * Controls whether the Prospects History and Export actions are shown to every role,
 * bypassing the importer-only restriction. Defaults to false — must be explicitly
 * enabled via VITE_IS_HISTORY_EXPORT_ENABLED_FOR_ALL_ROLES=true. Intended to be on
 * for dev/stage (so all roles can exercise the feature) and off in production,
 * where History/Export should only be visible to Importer users.
 * @returns true if History/Export should be shown to all roles, not just Importer
 */
export const isHistoryExportEnabledForAllRoles = (): boolean => {
  return import.meta.env.VITE_IS_HISTORY_EXPORT_ENABLED_FOR_ALL_ROLES === 'true';
};

/**
 * Controls whether the Prospect Details "Test Drive" tab supports multiple independent
 * test drives per prospect (list/schedule/modify/cancel each one separately), instead of
 * the legacy single-test-drive form.
 * Defaults to false — must be explicitly enabled via VITE_IS_MULTI_TEST_DRIVE_ENABLED=true.
 * @returns true if the multi test-drive feature is enabled
 */
export const isMultiTestDriveEnabled = (): boolean => {
  return import.meta.env.VITE_IS_MULTI_TEST_DRIVE_ENABLED === 'true';
};

/**
 * Controls whether the per-test-drive Check-In action (generating and signing the DocuSign
 * check-in agreement) is shown in both the multi and legacy single-test-drive tabs.
 * This is enabled via VITE_IS_TEST_DRIVE_CHECK_IN_ENABLED=true in dev only, and kept
 * disabled on stage/production until the check-in feature is ready to ship.
 * @returns true if the Check-In action is enabled
 */
export const isTestDriveCheckInEnabled = (): boolean => {
  return import.meta.env.VITE_IS_TEST_DRIVE_CHECK_IN_ENABLED === 'true';
};

/**
 * Controls whether the Assigned Sales Consultant dropdown is shown in the multi test-drive form.
 * Enabled via VITE_IS_TEST_DRIVE_HOST_ASSIGNMENT_ENABLED=true
 * @returns true if test drive host assignment is enabled
 */
export const isTestDriveHostAssignmentEnabled = (): boolean => {
  return import.meta.env.VITE_IS_TEST_DRIVE_HOST_ASSIGNMENT_ENABLED === 'true';
};

/** Offers the Extended Test Drive option next to Standard in the multi test-drive form. */
export const isExtendedTestDriveEnabled = (): boolean => {
  return import.meta.env.VITE_IS_EXTENDED_TEST_DRIVE_ENABLED === 'true';
};

/** Shows the "Charging Credit Offer" requested incentive on quotes. */
export const isChargingVoucherEnabled = (): boolean => {
  return import.meta.env.VITE_IS_CHARGING_VOUCHER_ENABLED === 'true';
};

/**
 * Bypasses SFDC/date visibility checks for development testing only.
 * Unset or empty values are disabled, including in stage and production.
 */
export const isTestDriveCheckInAlwaysVisible = (): boolean => {
  return (
    import.meta.env.MODE === 'development' &&
    import.meta.env.VITE_IS_TEST_DRIVE_CHECK_IN_ALWAYS_VISIBLE === 'true'
  );
};
